/* aviaf — Market Data page · candles + L2 + L3 + strategy overlay
 *
 * Phase 4: real data sourced from the agent's TickRecorder via the hub's
 * /api/market/* endpoints + live WS push.  Stores live on window.*:
 *   MARKET_CATALOG  — list of {symbol, contract, venue, tick, tick_value,
 *                     multiplier, name, quote?}
 *   MARKET_QUOTES   — sym → {ts_us, bid, ask, bid_sz, ask_sz, last_px, last_sz}
 *   MARKET_BARS     — sym → tf → [{ts_open_us, o, h, l, c, v, n_trades, closed}]
 *   MARKET_BOOK     — sym → {ts_us, bids:[{px,sz}], asks:[{px,sz}]}
 *   MARKET_PRINTS   — sym → [{ts_us, px, sz, side}, ...]  (newest first)
 *
 * The list page renders one card per catalog entry.  The detail page
 * lazy-loads bars/book/prints on mount via window.fetchMarket* and
 * subscribes implicitly to WS pushes via the useLiveData bump cycle.
 */
const { useState: useMkt, useEffect: useMktE, useMemo: useMktM, useRef: useMktR } = React;

/* Decimal places for a price given the contract tick size. */
function dpForTick(tick) {
  if (!tick || tick <= 0) return 2;
  if (tick < 0.001) return 5;
  if (tick < 0.01) return 4;
  if (tick < 0.1) return 3;
  if (tick < 1) return 2;
  return 1;
}

/* ── Candlestick + strategy overlay chart ── */
function CandleChart({ candles, markers, dp = 2, height = 420, showVolume = true }) {
  const ref = useMktR(null);
  const [hover, setHover] = useMkt(null);
  if (!candles.length) return null;

  const pad = { l: 56, r: 8, t: 12, b: showVolume ? 60 : 22 };
  const w = 920;
  const h = height;
  const chartH = h - pad.t - pad.b;
  const volH = showVolume ? 36 : 0;

  const lo = Math.min(...candles.map(c => c.l));
  const hi = Math.max(...candles.map(c => c.h));
  const range = hi - lo || 1;
  const xStep = (w - pad.l - pad.r) / candles.length;
  const cw = Math.max(2, xStep * 0.74);

  const yPx = p => pad.t + (1 - (p - lo) / range) * chartH;
  const xPx = i => pad.l + i * xStep + xStep / 2;

  const maxVol = Math.max(1, ...candles.map(c => c.v || 0));

  const yTicks = [];
  for (let i = 0; i <= 4; i++) {
    const p = lo + range * (i / 4);
    yTicks.push({ p, y: yPx(p) });
  }

  return (
    <div className="mkt-chart-wrap">
      <svg ref={ref} viewBox={`0 0 ${w} ${h}`} className="mkt-chart"
        onMouseMove={e => {
          const r = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - r.left) / r.width) * w;
          const i = Math.max(0, Math.min(candles.length - 1, Math.floor((x - pad.l) / xStep)));
          setHover(i);
        }}
        onMouseLeave={() => setHover(null)}>
        {yTicks.map((t, i) => (
          <g key={i}>
            <line x1={pad.l} x2={w - pad.r} y1={t.y} y2={t.y} stroke="var(--rule)" strokeDasharray="2 3" />
            <text x={pad.l - 6} y={t.y + 3} textAnchor="end" fontSize="9" fill="var(--muted)" fontFamily="var(--mono)">
              {t.p.toFixed(dp)}
            </text>
          </g>
        ))}
        {candles.map((c, i) => {
          const x = xPx(i);
          const up = c.c >= c.o;
          const yO = yPx(c.o), yC = yPx(c.c);
          const bodyTop = Math.min(yO, yC);
          const bodyH = Math.max(1, Math.abs(yC - yO));
          return (
            <g key={i}>
              <line x1={x} x2={x} y1={yPx(c.h)} y2={yPx(c.l)}
                stroke={up ? "var(--pos)" : "var(--neg)"} strokeWidth="1" />
              <rect x={x - cw / 2} y={bodyTop} width={cw} height={bodyH}
                fill={up ? "var(--pos)" : "var(--neg)"}
                opacity={c.closed === false ? 0.55 : (up ? 0.85 : 0.92)} />
            </g>
          );
        })}
        {showVolume && candles.map((c, i) => {
          const vy = h - pad.b + 4;
          const vh = ((c.v || 0) / maxVol) * volH * 0.86;
          const x = xPx(i);
          const up = c.c >= c.o;
          return <rect key={"v" + i} x={x - cw / 2} y={vy + (volH - vh)} width={cw} height={vh}
            fill={up ? "var(--pos)" : "var(--neg)"} opacity={0.36} />;
        })}
        {markers && markers.map((m, i) => {
          const x = xPx(m.idx);
          const y = yPx(m.px);
          const isEntry = m.kind === "entry";
          const isLong = m.side === "long";
          const color = m.pnl > 0 ? "var(--pos)" : m.pnl < 0 ? "var(--neg)" : "var(--accent)";
          return (
            <g key={"m" + i}>
              {isEntry ? (
                <>
                  <polygon
                    points={isLong
                      ? `${x},${y + 10} ${x - 5},${y + 18} ${x + 5},${y + 18}`
                      : `${x},${y - 10} ${x - 5},${y - 18} ${x + 5},${y - 18}`}
                    fill={color} stroke="var(--paper)" strokeWidth="1" />
                  <text x={x + 7} y={isLong ? y + 22 : y - 12} fontSize="9" fill={color} fontFamily="var(--mono)" fontWeight="600">
                    {isLong ? "L" : "S"}{m.contracts}
                  </text>
                </>
              ) : (
                <>
                  <circle cx={x} cy={y} r={4.5} fill="none" stroke={color} strokeWidth="1.6" />
                  <text x={x + 7} y={y + 3} fontSize="9" fill={color} fontFamily="var(--mono)" fontWeight="600">
                    {m.exitType || "X"}
                  </text>
                </>
              )}
              <line x1={x} x2={x} y1={pad.t} y2={h - pad.b} stroke={color} strokeWidth="0.6" strokeDasharray="2 3" opacity="0.35" />
            </g>
          );
        })}
        {hover != null && candles[hover] && (
          <g style={{ pointerEvents: "none" }}>
            <line x1={xPx(hover)} x2={xPx(hover)} y1={pad.t} y2={h - pad.b}
              stroke="var(--ink)" strokeDasharray="2 3" opacity="0.4" />
            <line x1={pad.l} x2={w - pad.r} y1={yPx(candles[hover].c)} y2={yPx(candles[hover].c)}
              stroke="var(--ink)" strokeDasharray="2 3" opacity="0.4" />
          </g>
        )}
      </svg>
      {hover != null && candles[hover] && (
        <div className="mkt-tip mono">
          <span>{new Date(candles[hover].ts_open_us / 1000).toLocaleString("en-US", { hour12: false })}</span>
          <span>O <b>{candles[hover].o.toFixed(dp)}</b></span>
          <span>H <b className="pos">{candles[hover].h.toFixed(dp)}</b></span>
          <span>L <b className="neg">{candles[hover].l.toFixed(dp)}</b></span>
          <span>C <b>{candles[hover].c.toFixed(dp)}</b></span>
          <span>V <b>{(candles[hover].v || 0).toLocaleString()}</b></span>
        </div>
      )}
    </div>
  );
}

function fmtPx(px, dp) {
  const s = (px || 0).toFixed(dp);
  const stable = s.slice(0, -2);
  const live = s.slice(-2);
  return <><span className="d">{stable}</span><span className="b">{live}</span></>;
}

/* ── Depth chart — cumulative liquidity step curve from live book ── */
function DepthChart({ book, midPx, dp, height = 520 }) {
  const asks = (book.asks || []).slice(0, 28);
  const bids = (book.bids || []).slice(0, 28);
  if (!asks.length || !bids.length) return null;

  const askCum = []; let s = 0;
  asks.forEach(a => { s += a.sz; askCum.push({ px: a.px, cum: s }); });
  const bidCum = []; s = 0;
  bids.forEach(b => { s += b.sz; bidCum.push({ px: b.px, cum: s }); });

  const maxCum = Math.max(askCum[askCum.length - 1].cum, bidCum[bidCum.length - 1].cum) * 1.05;
  const hi = asks[asks.length - 1].px;
  const lo = bids[bids.length - 1].px;
  const range = hi - lo || 1;

  const w = 220, h = height;
  const padL = 60, padR = 4, padT = 0;
  const chartW = w - padL - padR;
  const yPx = p => padT + (1 - (p - lo) / range) * h;
  const xPx = c => padL + (c / maxCum) * chartW;
  const midY = yPx(midPx);

  const askPath = [`M ${padL} ${midY}`];
  askCum.forEach((a, i) => {
    const y = yPx(a.px);
    const xPrev = xPx(i === 0 ? 0 : askCum[i - 1].cum);
    askPath.push(`L ${xPrev} ${y}`);
    askPath.push(`L ${xPx(a.cum)} ${y}`);
  });
  const askLast = askCum[askCum.length - 1];
  askPath.push(`L ${padL + chartW} ${yPx(askLast.px)}`);
  askPath.push(`L ${padL + chartW} ${midY} Z`);

  const bidPath = [`M ${padL} ${midY}`];
  bidCum.forEach((b, i) => {
    const y = yPx(b.px);
    const xPrev = xPx(i === 0 ? 0 : bidCum[i - 1].cum);
    bidPath.push(`L ${xPrev} ${y}`);
    bidPath.push(`L ${xPx(b.cum)} ${y}`);
  });
  const bidLast = bidCum[bidCum.length - 1];
  bidPath.push(`L ${padL + chartW} ${yPx(bidLast.px)}`);
  bidPath.push(`L ${padL + chartW} ${midY} Z`);

  return (
    <svg className="mkt-depth-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <path d={askPath.join(" ")} fill="var(--neg)" fillOpacity="0.18" stroke="var(--neg)" strokeWidth="1" />
      <path d={bidPath.join(" ")} fill="var(--pos)" fillOpacity="0.18" stroke="var(--pos)" strokeWidth="1" />
      <line x1={padL} x2={padL + chartW} y1={midY} y2={midY} stroke="var(--ink)" strokeWidth="0.6" opacity="0.5" />
      <text x={padL - 4} y={midY + 3} textAnchor="end" fontSize="9.5" fill="var(--ink)" fontFamily="var(--mono)">{midPx.toFixed(dp)}</text>
    </svg>
  );
}

/* ── L2 depth ladder from live book ── */
function DepthLadder({ book, midPx, ticker = "USD", tick }) {
  const dp = dpForTick(tick);
  const N = 12;
  const asks = (book.asks || []).slice(0, N);
  const bids = (book.bids || []).slice(0, N);
  if (!asks.length && !bids.length) {
    return (
      <div className="mkt-empty mono" style={{ padding: "32px 12px", color: "var(--muted)", fontSize: 11, textAlign: "center" }}>
        Depth feed not subscribed (record_depth=false on the agent).
      </div>
    );
  }
  const maxSz = Math.max(1, ...asks.map(a => a.sz || 0), ...bids.map(b => b.sz || 0));
  const fmtSz = sz => sz < 100 ? (sz || 0).toFixed(2) : sz.toFixed(0);

  const rowH = 22;
  const askH = asks.length * rowH;
  const bidH = bids.length * rowH;
  const midH = 38;
  const totalH = askH + midH + bidH;

  return (
    <div className="mkt-book-bookmap" style={{ height: totalH + "px" }}>
      <div className="mkt-depth-col">
        <DepthChart book={book} midPx={midPx} dp={dp} height={totalH} />
      </div>
      <div className="mkt-ladder-col">
        {[...asks].reverse().map((a, i) => (
          <div className="lad-row ask" key={"a" + i} style={{ height: rowH + "px" }}>
            <span className="bar" style={{ width: (a.sz / maxSz * 100) + "%" }} />
            <span className="px mono">{fmtPx(a.px, dp)}</span>
            <span className="sz mono tnum">{fmtSz(a.sz)}</span>
          </div>
        ))}
        <div className="lad-mid mono" style={{ height: midH + "px" }}>
          <span className="px-big">{midPx.toFixed(dp)}</span>
          <span className="ticker">{ticker}</span>
        </div>
        {bids.map((b, i) => (
          <div className="lad-row bid" key={"b" + i} style={{ height: rowH + "px" }}>
            <span className="bar" style={{ width: (b.sz / maxSz * 100) + "%" }} />
            <span className="px mono">{fmtPx(b.px, dp)}</span>
            <span className="sz mono tnum">{fmtSz(b.sz)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── L3 print tape from live prints ── */
function PrintTape({ prints, dp = 2 }) {
  if (!prints.length) {
    return (
      <div className="mkt-empty mono" style={{ padding: "16px 12px", color: "var(--muted)", fontSize: 11, textAlign: "center" }}>
        No prints yet — waiting for the next trade.
      </div>
    );
  }
  return (
    <div className="mkt-tape mono">
      <div className="mkt-tape-h">
        <span>time</span><span>side</span><span>price</span><span>sz</span>
      </div>
      {prints.slice(0, 30).map((p, i) => (
        <div className={"mkt-tape-row " + (p.side === "B" ? "buy" : p.side === "S" ? "sell" : "")} key={i}>
          <span className="muted tnum">{new Date(p.ts_us / 1000).toLocaleTimeString("en-US", { hour12: false })}</span>
          <span className="side">{p.side === "B" ? "BUY" : p.side === "S" ? "SELL" : "·"}</span>
          <span className="tnum px">{(p.px || 0).toFixed(dp)}</span>
          <span className="tnum">{(p.sz || 0).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Per-symbol catalog entry derivation ── */
function catalogEntry(sym) {
  const cat = window.MARKET_CATALOG || [];
  const found = cat.find(s => s.symbol === sym);
  if (found) return found;
  // Fall back to whatever we can synthesize from quotes alone — symbol
  // is at least known.
  return { symbol: sym, contract: "", venue: "", tick: 0, tick_value: 0, multiplier: 0, name: "" };
}

function lastPriceForSym(sym) {
  const q = (window.MARKET_QUOTES || {})[sym];
  if (q && typeof q.last_px === "number") return q.last_px;
  // Try the most recent bar close.
  const bars = ((window.MARKET_BARS || {})[sym] || {})["1m"] || [];
  if (bars.length) return bars[bars.length - 1].c;
  return null;
}

/* ── Market Data: list page ── */
window.PageMarket = function PageMarket() {
  const catalog = (window.MARKET_CATALOG || []);
  // Sorting: contracts with a live quote first, alphabetical within
  // each bucket.  Keeps "real" instruments on top when an old catalog
  // entry has gone stale.
  const rows = useMktM(() => {
    const arr = [...catalog];
    arr.sort((a, b) => {
      const ap = (window.MARKET_QUOTES || {})[a.symbol] ? 0 : 1;
      const bp = (window.MARKET_QUOTES || {})[b.symbol] ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return (a.symbol || "").localeCompare(b.symbol || "");
    });
    return arr;
  }, [catalog]);

  return (
    <div>
      <window.PageHeader
        crumbs={[{ label: "Market data" }]}
        actions={<><window.Btn>Subscribe</window.Btn><window.Btn kind="primary">+ Watchlist</window.Btn></>}
      />
      <div style={{ padding: "6px var(--pad) 0" }}>
        <window.SectionHeader title="Instruments" sub="live · from tick_recorder via /api/market/*" count={rows.length} />
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: "32px var(--pad)", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 12 }}>
          No symbols yet — the agent's symbol_catalog event hasn't arrived. This page populates as
          soon as the agent emits its first catalog snapshot (startup + hourly cadence).
        </div>
      ) : (
        <div className="mkt-grid">
          {rows.map(s => {
            const dp = dpForTick(s.tick);
            const last = lastPriceForSym(s.symbol);
            const bars = ((window.MARKET_BARS || {})[s.symbol] || {})["1m"] || [];
            const sparkData = bars.map(b => b.c);
            const first = sparkData[0];
            const chg = (last != null && first != null) ? last - first : 0;
            const chgPct = first ? chg / first : 0;
            const dayHi = bars.length ? Math.max(...bars.map(b => b.h)) : null;
            const dayLo = bars.length ? Math.min(...bars.map(b => b.l)) : null;
            const stratsHere = (window.STRATS || []).filter(st => st.symbol === s.symbol);
            return (
              <a className="mkt-card" key={s.symbol} href={`#/market/${s.symbol}`}>
                <div className="mkt-card-h">
                  <div>
                    <div className="sym">{s.symbol}</div>
                    <div className="contract mono muted">{s.contract || "—"}{s.venue ? ` · ${s.venue}` : ""}</div>
                  </div>
                  <div className="px-blk">
                    <div className={"px tnum " + (chg >= 0 ? "pos" : "neg")}>
                      {last != null ? last.toFixed(dp) : "—"}
                    </div>
                    <div className={"chg tnum " + (chg >= 0 ? "pos" : "neg")}>
                      {chg >= 0 ? "+" : ""}{chg.toFixed(dp)} ({(chgPct * 100).toFixed(2)}%)
                    </div>
                  </div>
                </div>
                {sparkData.length > 1 && (
                  <div className="mkt-card-spark">
                    <window.Sparkline data={sparkData} width={300} height={42} fill />
                  </div>
                )}
                <div className="mkt-card-meta mono">
                  {dayHi != null && <span>H <b>{dayHi.toFixed(dp)}</b></span>}
                  {dayLo != null && <span>L <b>{dayLo.toFixed(dp)}</b></span>}
                  {s.tick ? <span>tick <b>{s.tick}</b></span> : null}
                  {s.tick_value ? <span>${s.tick_value}/tick</span> : null}
                </div>
                {stratsHere.length > 0 && (
                  <div className="mkt-card-strats">
                    {stratsHere.map(st => (
                      <span key={st.id} className="pill" data-state={st.state}>{st.name} · {st.state}</span>
                    ))}
                  </div>
                )}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── Market Data: per-symbol detail ── */
window.PageMarketDetail = function PageMarketDetail({ id }) {
  const inst = catalogEntry(id);
  const [tf, setTf] = useMkt("5m");
  const [chartType, setChartType] = useMkt("candles");
  const [bookMode, setBookMode] = useMkt("L2");

  // Lazy fetch bars on tf change + book/prints on mount.  Each helper
  // writes into the shared window.MARKET_* store, so live WS pushes
  // continue to merge in place after the fetch completes.
  useMktE(() => { window.fetchMarketBars && window.fetchMarketBars(id, tf, { limit: 240 }); }, [id, tf]);
  useMktE(() => {
    window.fetchMarketBook && window.fetchMarketBook(id);
    window.fetchMarketPrints && window.fetchMarketPrints(id, { limit: 200 });
  }, [id]);

  const bars = ((window.MARKET_BARS || {})[id] || {})[tf] || [];
  const book = (window.MARKET_BOOK || {})[id] || { bids: [], asks: [] };
  const prints = (window.MARKET_PRINTS || {})[id] || [];
  const quote = (window.MARKET_QUOTES || {})[id] || {};

  const dp = dpForTick(inst.tick);
  const last = bars.length ? bars[bars.length - 1].c : (quote.last_px ?? null);

  // Strategies trading this symbol.
  const stratsHere = (window.STRATS || []).filter(s => s.symbol === id);
  const [overlayStrat, setOverlayStrat] = useMkt(stratsHere[0]?.id || null);

  // Synthetic-marker overlay: derive entries/exits from per-strategy
  // fills on this symbol.  Real wiring would come from the fills feed;
  // for now we leave the array empty so the chart isn't polluted with
  // fake markers when there are no real fills to display.
  const markers = [];
  const totalPnl = 0;
  const exitCount = 0;
  const wins = 0;

  if (!inst.symbol) {
    return <div style={{ padding: 32 }}>Instrument not found.</div>;
  }

  return (
    <div>
      <window.PageHeader
        crumbs={[{ label: "Market data", href: "#/market" }, { label: id }]}
        actions={<>
          <window.Btn>Replay</window.Btn>
          <window.Btn>Export bars</window.Btn>
          <window.Btn kind="primary">+ Strategy on {id}</window.Btn>
        </>}
      />

      {/* Hero strip */}
      <div className="mkt-hero">
        <div className="mkt-hero-l">
          <div className="sym">{inst.symbol}</div>
          <div className="contract mono">
            {inst.contract || "—"}{inst.venue ? ` · ${inst.venue}` : ""}
            {inst.name ? ` · ${inst.name}` : ""}
          </div>
        </div>
        <div className="mkt-hero-px">
          <div className={"px tnum " + ((last ?? 0) >= (bars[0]?.o ?? 0) ? "pos" : "neg")}>
            {last != null ? last.toFixed(dp) : "—"}
          </div>
          <div className="chg mono">
            {bars.length > 0 && last != null && (
              <span className={last - bars[0].o >= 0 ? "pos" : "neg"}>
                {last - bars[0].o >= 0 ? "+" : ""}{(last - bars[0].o).toFixed(dp)}
              </span>
            )}
            <span className="muted">
              tick {inst.tick || "—"}
              {inst.tick_value ? ` · $${inst.tick_value}/tick` : ""}
            </span>
          </div>
        </div>
        <div className="mkt-hero-stats mono">
          <div><span className="lbl">bid</span><b className="bid tnum">{quote.bid != null ? quote.bid.toFixed(dp) : "—"}</b></div>
          <div><span className="lbl">ask</span><b className="ask tnum">{quote.ask != null ? quote.ask.toFixed(dp) : "—"}</b></div>
          <div><span className="lbl">spread</span><b className="tnum">{(quote.bid != null && quote.ask != null) ? (quote.ask - quote.bid).toFixed(dp) : "—"}</b></div>
          <div><span className="lbl">vol</span><b className="tnum">{bars.reduce((s, c) => s + (c.v || 0), 0).toLocaleString()}</b></div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="mkt-toolbar">
        <div className="seg">
          {["1m", "5m", "15m", "1h", "1d"].map(k => (
            <button key={k} data-active={tf === k} onClick={() => setTf(k)}>{k}</button>
          ))}
        </div>
        <div className="seg">
          {[["candles", "Candles"], ["line", "Line"]].map(([k, l]) => (
            <button key={k} data-active={chartType === k} onClick={() => setChartType(k)}>{l}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <div className="seg">
          {[["L2", "L2 depth"], ["L3", "L3 prints"], ["both", "both"]].map(([k, l]) => (
            <button key={k} data-active={bookMode === k} onClick={() => setBookMode(k)}>{l}</button>
          ))}
        </div>
      </div>

      {/* Strategy overlay selector */}
      {stratsHere.length > 0 && (
        <div className="mkt-overlay-bar">
          <span className="lbl mono muted">overlay strategy:</span>
          <button className={"pill " + (overlayStrat === null ? "active" : "")} onClick={() => setOverlayStrat(null)}>none</button>
          {stratsHere.map(s => (
            <button key={s.id} className={"pill " + (overlayStrat === s.id ? "active" : "")} data-state={s.state}
              onClick={() => setOverlayStrat(s.id)}>
              {s.name} · {s.acct} · {s.state}
            </button>
          ))}
          {overlayStrat && exitCount > 0 && (
            <span className="mono muted" style={{ marginLeft: "auto", fontSize: 11 }}>
              {exitCount} trades · {wins}W/{exitCount - wins}L · est P&L <b className={totalPnl >= 0 ? "pos" : "neg"}>{window.fmt$(totalPnl, 0)}</b>
            </span>
          )}
        </div>
      )}

      {/* Main grid: chart + book */}
      <div className="mkt-detail-grid" data-mode={bookMode}>
        <div className="mkt-chart-col">
          {bars.length === 0 ? (
            <div className="mkt-empty mono" style={{ padding: "48px 12px", color: "var(--muted)", fontSize: 12, textAlign: "center" }}>
              No bars yet for {id} ({tf}). Bars populate ~1 minute after the first trade tick reaches the agent.
            </div>
          ) : (
            <CandleChart candles={bars} markers={markers} dp={dp} height={460} showVolume />
          )}
        </div>
        {bookMode !== "none" && (
          <div className="mkt-book-col">
            {(bookMode === "L2" || bookMode === "both") && (
              <div className="mkt-book-section">
                <div className="mkt-book-h mono">L2 · order book + depth</div>
                <DepthLadder book={book} midPx={last || 0} ticker={inst.symbol} tick={inst.tick} />
              </div>
            )}
            {(bookMode === "L3" || bookMode === "both") && (
              <div className="mkt-book-section">
                <div className="mkt-book-h mono">L3 · time & sales</div>
                <PrintTape prints={prints} dp={dp} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Strategies trading this symbol */}
      <window.SectionHeader title={`Strategies on ${id}`} count={stratsHere.length} />
      {stratsHere.length === 0 ? (
        <div style={{ padding: "24px var(--pad)", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 12 }}>
          No active strategies on this instrument.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="strat">
            <thead><tr>
              <th>Strategy</th><th>Account</th><th>State</th><th className="num">Contracts</th>
              <th className="num">WR</th><th className="num">PF</th><th className="num">30d P&L</th><th className="num">Today</th>
            </tr></thead>
            <tbody>
              {stratsHere.map(s => (
                <tr key={s.id} className="row-link" onClick={() => { location.hash = `#/accounts/${s.acct}`; }}>
                  <td>{s.name}</td>
                  <td className="mono muted">{s.acct}</td>
                  <td><window.StateBadge state={s.state} /></td>
                  <td className="num tnum">{s.contracts}</td>
                  <td className="num tnum">{(s.wr * 100).toFixed(1)}%</td>
                  <td className="num tnum">{s.pf.toFixed(2)}</td>
                  <td className={"num tnum " + (s.pnl30 >= 0 ? "pos" : "neg")}>{window.fmt$(s.pnl30, 0)}</td>
                  <td className={"num tnum " + (s.today > 0 ? "pos" : s.today < 0 ? "neg" : "muted")}>
                    {s.today === 0 ? "—" : window.fmt$(s.today, 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
